import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowRight, ShieldCheck, Sparkles, Scale, Home as HomeIcon, Gavel, Building2, Heart,
  Search, Star, Lock, FileCheck2, MessageSquare, Clock, Zap, XCircle, Video, PhoneCall, BadgeCheck, Languages,
} from "lucide-react";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import Testimonials from "@user/components/Testimonials";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useExperiment } from "@/lib/experiment";

const categories = [
  { icon: Heart, title: "Family", sub: "Divorce, custody", spec: "Family Law", cta: "Talk to Family Lawyer", color: "#F472B6" },
  { icon: HomeIcon, title: "Property", sub: "Rent, disputes", spec: "Property Law", cta: "Talk to Property Lawyer", color: "#60A5FA" },
  { icon: Gavel, title: "Criminal", sub: "FIR, bail", spec: "Criminal Law", cta: "Talk to Criminal Lawyer", color: "#F59E0B" },
  { icon: Building2, title: "Corporate", sub: "MSME, GST", spec: "Corporate Law", cta: "Talk to Corporate Lawyer", color: "#10B981" },
];

const trustItems = [
  { icon: ShieldCheck, label: "Verified Lawyers Only" },
  { icon: Lock, label: "Secure Payments" },
  { icon: FileCheck2, label: "100% Confidential" },
  { icon: Scale, label: "Bar Council Registered" },
];

const pricingTiers = [
  {
    icon: MessageSquare, title: "Chat", range: "₹199 – ₹299", sub: "15-min text consultation",
    bullets: ["Quick answers", "Fast turnaround", "Most affordable"],
    accent: "#0A2540",
  },
  {
    icon: PhoneCall, title: "Audio Call", range: "₹299 – ₹999", sub: "20-min voice call",
    bullets: ["Talk in Hindi or English", "Real-time clarifications", "Great for complex queries"],
    accent: "#D4AF37",
    popular: true,
  },
  {
    icon: Video, title: "Video Call", range: "₹499 – ₹1500", sub: "30-min face-to-face",
    bullets: ["Most personal", "Review documents on call", "Senior lawyer access"],
    accent: "#10B981",
  },
];

const stats = [
  { v: "2,400+", l: "legal questions answered" },
  { v: "150+", l: "verified lawyers" },
  { v: "50+", l: "cities covered" },
  { v: "4.8/5", l: "average rating" },
];

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2 bg-slate-100 rounded-2xl rounded-tl-sm w-fit">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" />
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: "120ms" }} />
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: "240ms" }} />
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const [query, setQuery] = React.useState("");
  const [topLawyers, setTopLawyers] = useState([]);
  const [lawStats, setLawStats] = useState({ online: 12, avg_response_mins: 2 });

  // A/B test: chat entry price (₹99 vs ₹199)
  const { variant, track } = useExperiment("chat_entry_price", ["A", "B"]);
  const chatPrice = variant === "B" ? 199 : 99;
  const consultPrice = variant === "B" ? 399 : 299;

  // A/B test: CTA copy (Consult Now vs Book Now)
  const { variant: ctaVariant, track: trackCta } = useExperiment("cta_copy", ["consult_now", "book_now"]);
  const ctaLabel = ctaVariant === "book_now" ? "Book Now" : "Consult Now";

  // A/B test: hero headline
  const { variant: headlineVariant, track: trackHeadline } = useExperiment("hero_headline", ["talk_to_lawyer", "legal_help_5min"]);

  useEffect(() => {
    api.get("/lawyers", { params: { available_only: true } })
      .then((r) => setTopLawyers((r.data || []).slice(0, 4)))
      .catch(() => setTopLawyers([]));
    api.get("/lawyers-stats").then((r) => setLawStats(r.data)).catch(() => {});
  }, []);

  // Scroll to hash anchor when navigating to /#pricing or /#how-it-works
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [location.hash]);

  const onAsk = () => {
    if (query.trim()) sessionStorage.setItem("adv_initial_q", query.trim());
    navigate("/chat");
  };

  const onBook = (l) => {
    track("cta_click");
    trackCta("cta_click");
    trackHeadline("cta_click");
    if (!user) { login(); return; }
    navigate(`/book/${l.id}?type=chat`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden adv-navy">
        <div className="adv-blob bg-[#D4AF37]" style={{ width: 480, height: 480, top: -140, right: -120, opacity: 0.22 }} />
        <div className="adv-blob bg-[#1E40AF]" style={{ width: 420, height: 420, bottom: -180, left: -120, opacity: 0.35 }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 lg:pt-24 lg:pb-20 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 adv-fade-in text-white">
            <span className="adv-chip" data-testid="hero-chip">
              <Sparkles size={14} /> India-first · AI + Lawyers
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" style={{ fontFamily: "Poppins" }} data-testid={`hero-headline-${headlineVariant || "default"}`}>
              {headlineVariant === "legal_help_5min" ? (
                <>
                  <span className="adv-gold-text">Legal Help</span>
                  <br /> <span className="text-white/95">in 5 Minutes.</span>
                </>
              ) : (
                <>
                  Talk to a <span className="adv-gold-text">Lawyer</span>
                  <br /> <span className="text-white/95">Instantly.</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
              Chat, call, or meet verified lawyers across India — <span className="font-bold text-[#D4AF37]">starting at ₹{chatPrice}</span>.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/lawyers" onClick={() => { track("cta_click"); trackCta("cta_click"); trackHeadline("cta_click"); }}>
                <Button data-testid="hero-consult-cta" className="rounded-full adv-btn-gold font-bold h-12 px-7 text-base shadow-lg shadow-black/20">
                  {ctaLabel} <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
              <Link to="/chat">
                <Button variant="outline" data-testid="hero-guidance-cta" className="rounded-full h-12 px-7 text-base border-white/30 bg-white/5 text-white hover:bg-white/10 hover:text-white font-semibold">
                  Get Quick Guidance (Free)
                </Button>
              </Link>
            </div>

            {/* Risk reversal line */}
            <p className="mt-4 text-sm text-white/85 font-medium flex items-center gap-1.5" data-testid="risk-reversal-line">
              <MessageSquare size={14} className="text-[#D4AF37]" /> Start chat for free. Pay only if you continue.
            </p>

            {/* Privacy reassurance */}
            <p className="mt-2 text-sm text-white/80 font-medium flex items-center gap-1.5" data-testid="privacy-reassurance-line">
              <Lock size={14} className="text-emerald-400" /> Your legal queries are <b>100% private &amp; encrypted</b>. Never shared.
            </p>

            {/* Social proof line */}
            <p className="mt-2 text-sm text-white/85 font-semibold flex items-center gap-1.5" data-testid="social-proof-line">
              <Sparkles size={14} className="text-[#D4AF37]" /> Trusted by <b className="text-[#D4AF37]">2,400+ users</b> across India</p>

            {/* Trust + pricing strip */}
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/85" data-testid="hero-trust-strip">
              <span className="inline-flex items-center gap-1.5"><Star size={14} className="text-[#D4AF37]" fill="currentColor" /> <b>4.8/5</b> average rating</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-400" /> <b>150+</b> verified lawyers</span>
              <span className="inline-flex items-center gap-1.5"><MessageSquare size={14} className="text-[#D4AF37]" /> Chat starting <b>₹{chatPrice}</b></span>
            </div>

            {/* Urgency indicators */}
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85" data-testid="urgency-strip">
              <span className="inline-flex items-center gap-1.5">
                <span className="adv-pulse-dot" /> <b className="text-emerald-400">{lawStats.online || 12}</b> lawyers available right now
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Zap size={14} className="text-[#D4AF37]" /> Avg response time: <b>{lawStats.avg_response_mins || 2} mins</b>
              </span>
            </div>

            {/* Search (secondary) */}
            <div className="mt-7 flex flex-col sm:flex-row gap-2 max-w-2xl bg-white rounded-2xl p-2 shadow-xl shadow-black/10">
              <div className="flex-1 flex items-center gap-2 px-3">
                <Search size={16} className="text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onAsk()}
                  placeholder="Describe your legal issue (optional)…"
                  data-testid="hero-search-input"
                  className="border-0 shadow-none focus-visible:ring-0 text-[#0A2540] placeholder:text-slate-400"
                />
              </div>
              <Button
                onClick={onAsk}
                data-testid="hero-ask-cta"
                variant="outline"
                className="rounded-xl font-bold h-10 px-5 border-slate-200 text-[#0A2540]"
              >
                Ask for free
              </Button>
            </div>

            {/* Final trust boost near CTA */}
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/75 font-medium" data-testid="cta-trust-boost">
              <span className="inline-flex items-center gap-1.5"><Lock size={12} className="text-[#D4AF37]" /> 100% private & confidential</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-400" /> Verified lawyers only</span>
              <span className="inline-flex items-center gap-1.5"><XCircle size={12} className="text-white/60" /> No obligation to continue</span>
            </div>
          </div>

          {/* Chat preview */}
          <div className="lg:col-span-5 adv-fade-in">
            <div className="relative rounded-3xl bg-white p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-full adv-navy grid place-items-center"><Scale size={14} className="text-[#D4AF37]" /></div>
                  <div>
                    <p className="text-sm font-bold text-[#0A2540]">Adv. Priya Sharma</p>
                    <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1"><span className="adv-pulse-dot" /> Online · typing…</span>
                  </div>
                </div>
                <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Live chat</span>
              </div>
              <div className="space-y-2">
                <div className="ml-auto max-w-[85%] bg-[#0A2540] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
                  My landlord is asking me to vacate without notice. What should I do?
                </div>
                <TypingDots />
                <div className="max-w-[90%] bg-[#FBF4DF] text-[#0A2540] rounded-2xl rounded-tl-sm px-4 py-3 border border-[#F1E3B5] text-sm">
                  Don't worry — eviction requires proper written notice. Keep paying rent via bank/UPI to keep proof.
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button onClick={() => navigate("/chat")} data-testid="preview-continue-chat" className="rounded-xl adv-btn-primary text-sm font-bold py-2.5">
                  Start Free Chat →
                </button>
                <button onClick={() => { track("cta_click"); navigate("/lawyers"); }} data-testid="preview-book-15min" className="rounded-xl adv-btn-gold text-sm font-bold py-2.5">
                  Book 15-min Consult
                </button>
              </div>
              <p className="mt-2 text-[11px] text-slate-400 text-center">Free for first message · Chat from ₹{chatPrice} · Consult from ₹{consultPrice}</p>
            </div>
          </div>
        </div>

        {/* Trust bar heading + strip */}
        <div className="relative border-t border-white/10 bg-[#0A2540]/70 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <p className="text-center text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
              Trusted & Verified Across India
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trust-bar">
              {trustItems.map((t) => (
                <div key={t.label} className="flex items-center justify-center md:justify-start gap-2 text-xs sm:text-sm text-white/85 font-semibold">
                  <t.icon size={16} className="text-[#D4AF37] shrink-0" />
                  {t.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Browse by topic</span>
            <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>What do you need help with?</h2>
          </div>
          <Link to="/lawyers" className="hidden md:inline text-sm font-bold text-[#0A2540] hover:text-[#D4AF37]">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c) => (
            <Link
              to={`/lawyers?spec=${encodeURIComponent(c.spec)}`}
              key={c.title}
              data-testid={`category-${c.title.toLowerCase()}`}
              className="group bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${c.color}20`, color: c.color }}>
                <c.icon size={22} />
              </div>
              <h3 className="mt-4 text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{c.title}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{c.sub}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#D4AF37] group-hover:gap-2 transition-all">{c.cta} →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* TOP LAWYERS AVAILABLE NOW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 lg:pb-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600 inline-flex items-center gap-2">
              <span className="adv-pulse-dot" /> Online now
            </span>
            <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Top Lawyers Available Now</h2>
          </div>
          <Link to="/lawyers" className="hidden md:inline text-sm font-bold text-[#0A2540] hover:text-[#D4AF37]">See all lawyers →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="top-lawyers-grid">
          {topLawyers.length === 0 && Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 h-[320px] adv-skeleton" />
          ))}
          {topLawyers.map((l, idx) => {
            const initials = l.name.split(" ").slice(-2).map((x) => x[0]).join("");
            const respondMins = [1, 2, 2, 3][idx % 4];
            return (
              <div
                key={l.id}
                data-testid={`top-lawyer-${l.id}`}
                className="group bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-xl hover:-translate-y-1 transition-all relative"
              >
                <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                  <span className="adv-pulse-dot" /> Online
                </div>
                <div className="flex flex-col items-center text-center pt-3">
                  {l.photo ? (
                    <img src={l.photo} alt={l.name} className="h-16 w-16 rounded-full object-cover border-2 border-[#D4AF37]" />
                  ) : (
                    <div className="h-16 w-16 rounded-full adv-navy text-[#D4AF37] grid place-items-center font-bold text-lg border-2 border-[#D4AF37]">
                      {initials}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-1">
                    <h3 className="font-bold text-[#0A2540] text-sm leading-tight" style={{ fontFamily: "Poppins" }}>{l.name}</h3>
                    {l.verified && <BadgeCheck size={12} className="text-[#D4AF37]" />}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{l.specialization}</p>
                  <div className="mt-2 flex flex-wrap justify-center items-center gap-1.5 text-[11px]">
                    <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded font-bold">
                      <Star size={10} fill="currentColor" />{l.rating.toFixed(1)}
                    </span>
                    <span className="text-slate-500">({l.reviews_count || 120})</span>
                    <span className="text-slate-500">· {l.experience}y</span>
                  </div>
                  <div className="mt-2 flex flex-wrap justify-center items-center gap-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Clock size={10} className="text-emerald-600" /> Responds in ~{respondMins} min</span>
                  </div>
                  {l.languages?.length > 0 && (
                    <p className="mt-1 text-[11px] text-slate-500 inline-flex items-center gap-1 truncate max-w-full">
                      <Languages size={10} /> {l.languages.slice(0, 2).join(" · ")}
                    </p>
                  )}
                  <p className="mt-3 text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
                    ₹{l.fee}<span className="text-[11px] font-medium text-slate-500"> /consult</span>
                  </p>
                  <Button
                    onClick={() => onBook(l)}
                    data-testid={`top-chat-${l.id}`}
                    className="mt-3 w-full rounded-full adv-btn-primary font-bold"
                  >
                    <MessageSquare size={14} className="mr-1.5" /> Chat Now
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 lg:pb-20 scroll-mt-20">
        <div className="text-center mb-8">
          <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Pricing</span>
          <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Simple, Transparent Pricing</h2>
          <p className="mt-2 text-sm text-slate-500">No hidden fees · Pay per consultation · GST included</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4" data-testid="pricing-tiers">
          {pricingTiers.map((t) => (
            <div
              key={t.title}
              data-testid={`pricing-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative rounded-2xl p-6 border-2 transition-all ${t.popular ? "border-[#D4AF37] bg-[#FBF4DF]/40 shadow-xl" : "border-slate-200 bg-white hover:border-[#D4AF37] hover:shadow-lg"}`}
            >
              {t.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A2540] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  Most popular
                </span>
              )}
              <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${t.accent}20`, color: t.accent }}>
                <t.icon size={22} />
              </div>
              <h3 className="mt-4 text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{t.title}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{t.sub}</p>
              <p className="mt-3 text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{t.range}</p>
              <ul className="mt-4 space-y-2">
                {t.bullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                    <ShieldCheck size={14} className="text-emerald-600 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <Link to="/lawyers" onClick={() => { track("cta_click"); trackCta("cta_click"); }}>
                <Button className={`mt-5 w-full rounded-full font-bold h-10 ${t.popular ? "adv-btn-gold" : "adv-btn-primary"}`}>
                  {ctaLabel}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-20">
        <div className="rounded-3xl adv-navy text-white p-8 lg:p-12 relative overflow-hidden">
          <div className="adv-blob bg-[#D4AF37]" style={{ width: 400, height: 400, top: -180, right: -100, opacity: 0.25 }} />
          <div className="relative">
            <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">How it works</span>
            <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold" style={{ fontFamily: "Poppins" }}>Three simple steps</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {[
                { n: "01", t: "Describe your problem", d: "Tell us your issue in 30 seconds — no legal jargon needed." },
                { n: "02", t: "Chat with verified lawyers", d: "Connect instantly with lawyers who specialise in your matter." },
                { n: "03", t: "Pay only if you want to continue", d: "Start with a free AI guide. Upgrade to a paid consult only when ready." },
              ].map((s) => (
                <div key={s.n} className="relative">
                  <div className="text-5xl font-black text-[#D4AF37]/40" style={{ fontFamily: "Poppins" }}>{s.n}</div>
                  <h3 className="mt-2 text-xl font-bold" style={{ fontFamily: "Poppins" }}>{s.t}</h3>
                  <p className="mt-1 text-sm text-white/70 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link to="/lawyers" onClick={() => { track("cta_click"); trackCta("cta_click"); }}>
                <Button data-testid="how-cta-consult" className="rounded-full adv-btn-gold font-bold h-11 px-6">{ctaLabel}</Button>
              </Link>
              <Link to="/chat">
                <Button variant="outline" data-testid="how-cta-ai" className="rounded-full h-11 px-6 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold">
                  Get Quick Legal Guidance (Free)
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          {stats.map((s) => (
            <div key={s.l} className="text-center" data-testid={`stat-${s.v.replace(/\W/g, "")}`}>
              <p className="text-2xl md:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{s.v}</p>
              <p className="text-[11px] md:text-xs font-semibold text-slate-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      <Footer />

      {/* Sticky mobile bottom CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-slate-200 p-3 shadow-2xl" data-testid="mobile-sticky-cta">
        <Link to="/lawyers" className="block" onClick={() => { track("cta_click"); trackCta("cta_click"); trackHeadline("cta_click"); }}>
          <Button className="w-full min-h-[48px] rounded-full adv-btn-gold font-bold flex flex-col items-center leading-tight gap-0 py-2">
            <span className="text-base">{ctaLabel}</span>
            <span className="text-[11px] font-semibold opacity-75">Starting ₹{chatPrice}</span>
          </Button>
        </Link>
      </div>
      <div className="md:hidden h-20" />
    </div>
  );
}
